import type { ControlPeriod } from './types';

/**
 * The span itself, without any qualifier: "2026" for a calendar year,
 * "2025-06-15 – 2026-06-15" for a rolling period. The one place the two
 * modes are told apart for display; the sentences below and the control
 * header's meta item build on it.
 *
 * @param period The resolved period.
 * @param formatDay Renders a `YYYY-MM-DD` day for the rolling span; defaults to the day as is.
 */
export function controlPeriodSpan(
  period: ControlPeriod,
  formatDay: (day: string) => string = (day) => day,
): string {
  return period.mode === 'ROLLING_MONTHS'
    ? `${formatDay(period.startDate)} – ${formatDay(period.endDate)}`
    : String(period.mainYear);
}

/** "perioden A – B" for a rolling period, the bare year otherwise. */
const periodPhrase = (period: ControlPeriod, formatDay: (day: string) => string): string =>
  `${period.mode === 'ROLLING_MONTHS' ? 'perioden ' : ''}${controlPeriodSpan(period, formatDay)}`;

/**
 * The period phrase on its own: "2026", "preliminärt 2025" or
 * "perioden 2025-06-15 – 2026-06-15". The admin previews embed it in their
 * own sentence; {@link formatControlPeriod} is the farmer-facing sentence.
 *
 * @param period The resolved period.
 * @param formatDay Renders a `YYYY-MM-DD` day for the rolling span; defaults to the day as is.
 */
export function describeControlPeriod(
  period: ControlPeriod,
  formatDay: (day: string) => string = (day) => day,
): string {
  const qualifier = period.isEstimated ? 'preliminärt ' : '';
  return `${qualifier}${periodPhrase(period, formatDay)}`;
}

/**
 * The explanation both control views show for the resolved period, generated
 * from the model rather than authored per year (AG-289, AG-257). It says what
 * the period means for the farmer — which underlag the inspector looks at —
 * and, for an estimate, why it is preliminary and what settles it:
 *
 *   "Inspektören tittar på underlag för 2026."
 *   "Inspektören tittar på underlag för 2025. Året är preliminärt tills kontrollen har ett datum."
 *   "Inspektören tittar på underlag för perioden 2025-06-15 – 2026-06-15."
 *
 * @param period The resolved period.
 * @param formatDay Renders a `YYYY-MM-DD` day for the rolling span; defaults to the day as is.
 */
export function formatControlPeriod(
  period: ControlPeriod,
  formatDay: (day: string) => string = (day) => day,
): string {
  const rolling = period.mode === 'ROLLING_MONTHS';
  const sentence = `Inspektören tittar på underlag för ${periodPhrase(period, formatDay)}.`;
  if (!period.isEstimated) return sentence;
  return `${sentence} ${rolling ? 'Perioden är preliminär' : 'Året är preliminärt'} tills kontrollen har ett datum.`;
}

/**
 * Short label for a set of years: consecutive runs collapse into a range.
 * [2022, 2023, 2024, 2025] → "2022–2025"; [2021, 2023, 2024] → "2021, 2023–2024".
 * Input may be unsorted or contain duplicates; an empty list yields "".
 */
export function formatYearList(years: readonly number[]): string {
  const sorted = Array.from(new Set(years)).sort((a, b) => a - b);
  const runs: Array<[number, number]> = [];
  for (const year of sorted) {
    const last = runs[runs.length - 1];
    if (last && year === last[1] + 1) last[1] = year;
    else runs.push([year, year]);
  }
  return runs.map(([from, to]) => (from === to ? String(from) : `${from}–${to}`)).join(', ');
}
